import { useEffect, useState } from 'react';
import { api } from './api.ts';

export type Kind = 'ROLE' | 'HARD_SKILL' | 'SOFT_SKILL' | 'POS' | 'RESERVATION_TOOL' | 'CERTIFICATION' | 'LANGUAGE' | 'EQUIPMENT' | 'SHIFT_TYPE' | 'TRANSPORT';

export interface CatalogItem {
  id: string;
  kind: Kind;
  code: string;
  label: string;
  group: string | null;
  active: boolean;
  sortOrder: number;
}

export interface Catalog {
  items: CatalogItem[];
  roleSkills: Record<string, string[]>;
  seniority: Record<string, string>;
  languageLevels: Record<string, string>;
}

export const KIND_LABELS: Record<Kind, string> = {
  ROLE: 'Postes',
  HARD_SKILL: 'Compétences techniques',
  SOFT_SKILL: 'Savoir-être',
  POS: 'Logiciels de caisse',
  RESERVATION_TOOL: 'Logiciels de réservation',
  CERTIFICATION: 'Certifications et diplômes',
  LANGUAGE: 'Langues',
  EQUIPMENT: 'Équipements personnels',
  SHIFT_TYPE: 'Types de shifts',
  TRANSPORT: 'Moyens de transport',
};

export function useCatalog() {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  useEffect(() => {
    api.get<Catalog>('/catalog').then(setCatalog);
  }, []);
  return catalog;
}

export const ofKind = (catalog: Catalog, kind: Kind) => catalog.items.filter((i) => i.kind === kind);

export function groupBy<T>(list: T[], key: (t: T) => string | null): [string, T[]][] {
  const map = new Map<string, T[]>();
  for (const item of list) {
    const k = key(item) ?? '';
    map.set(k, [...(map.get(k) ?? []), item]);
  }
  return [...map.entries()];
}

// Ce qu'un établissement voit d'un extra (voir server/catalog.ts, publicPro).
export interface PublicPro {
  roles: { label: string; level: string; years: number | null; maxCovers: number | null }[];
  skills: string[];
  pos: string[];
  reservationTools: string[];
  languages: { label: string; level: string }[];
  certifications: string[];
  equipment: string[];
  transports: string[];
  shifts: string[];
  mobility: { drivingLicense: boolean; ownVehicle: boolean; maxTravelKm: number };
}

export interface MatchReason {
  criterion: string;
  label: string;
  status: 'ok' | 'partial' | 'missing';
  blocking: boolean;
}

export interface Match {
  score: number | null;
  blocked: boolean;
  blockers: string[];
  reasons: MatchReason[];
}

export interface Requirement {
  itemId: string;
  kind: Kind;
  code: string;
  label: string;
  importance: 'REQUIRED' | 'PREFERRED';
  minLevel: string | null;
}

export const SENIORITY_LABELS: Record<string, string> = { JUNIOR: 'Junior', CONFIRME: 'Confirmé', EXPERT: 'Expert' };
export const LANGUAGE_LEVEL_LABELS: Record<string, string> = { NOTIONS: 'Notions', INTERMEDIAIRE: 'Intermédiaire', COURANT: 'Courant', BILINGUE: 'Bilingue ou natif' };
